import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparks } from "iconoir-react";
import { api } from "@/lib/api";
import { useOrg, hasRole } from "@/lib/orgContext";

// Inline quota call-out shown at the point of intent (e.g. /projects, /audits/new).
// Resource keys: "projects" | "members" | "audits_monthly".
//
// Renders nothing when:
//   - no active organization
//   - billing endpoint fails / not loaded yet (silent — never blocks the page)
//   - quota is below 60% (not worth a nudge)
//
// At ≥60% renders an informational strip; at ≥80% emphasised; at 100% blocks-style call-out.
export default function QuotaInline({ resource, period = "month" }) {
  const { activeOrgId, activeRole } = useOrg();
  const canUpgrade = hasRole(activeRole, "admin");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!activeOrgId) return undefined;
    let cancelled = false;
    api
      .get(`/v1/organizations/${activeOrgId}/billing`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [activeOrgId]);

  if (!activeOrgId || !data?.limits || !data?.usage) return null;

  // Map resource → (used, allowed, label).
  const map = {
    projects: { used: data.usage.projects, allowed: data.limits.projects_max, noun: "projects" },
    members: { used: data.usage.members, allowed: data.limits.members_max, noun: "members" },
    audits_monthly: { used: data.usage.audits_this_month, allowed: data.limits.audits_monthly, noun: "audits" },
  };
  const m = map[resource];
  if (!m || m.allowed <= 0) return null;

  const remaining = Math.max(0, m.allowed - m.used);
  const pct = (m.used / m.allowed) * 100;
  if (pct < 60) return null;

  const isBlocked = remaining <= 0;
  const isHot = pct >= 80;
  const tone = isBlocked
    ? "border-intl bg-intl/5 text-intl"
    : isHot
    ? "border-amber-500 bg-amber-50 text-amber-900"
    : "border-border bg-card text-foreground";

  const periodSuffix = resource === "audits_monthly" ? ` this ${period}` : "";
  const message = isBlocked
    ? `You've used all ${m.allowed} ${m.noun}${periodSuffix} on the ${data.plan_key} plan.`
    : `You have ${remaining} of ${m.allowed} ${m.noun}${periodSuffix} remaining on the ${data.plan_key} plan.`;

  return (
    <div
      data-testid={`quota-inline-${resource}`}
      data-severity={isBlocked ? "exceeded" : isHot ? "warning" : "info"}
      className={`border ${tone} px-4 py-3 mb-4 flex items-center justify-between gap-3 text-sm`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <Sparks width={14} height={14} strokeWidth={1.8} />
        <span className="truncate font-app">
          {message}
          {isHot && !isBlocked && <span className="ml-1 font-mono text-[10px] uppercase tracking-widest">· nearing limit</span>}
        </span>
      </div>
      {canUpgrade && (
        <Link
          to="/billing"
          data-testid={`quota-inline-${resource}-upgrade`}
          className={`flex items-center gap-1 px-3 py-1.5 text-[11px] font-app uppercase tracking-widest whitespace-nowrap ${isBlocked ? "bg-intl text-white" : "bg-obsidian text-bone"} hover:opacity-90`}
        >
          Upgrade <ArrowRight width={12} height={12} />
        </Link>
      )}
    </div>
  );
}
